import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { MatSidenav } from '@angular/material/sidenav';
import {
  trigger,
  transition,
  style,
  animate,
  query,
} from '@angular/animations';
import { BreakpointObserver } from '@angular/cdk/layout';
import { select, Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';

// Store y Servicios
import * as fromRoot from './store';
import * as fromUser from './store/user';

import { SidenavStateService } from './shared/services/sidenav-state.service';
import { WebSocketService } from './services/websocket/WebSocketService';
import { InactivityService } from './services/inactivity.service';
import { AuthInterceptor } from './interceptors/auth-interceptor';
import { TokenService } from './services/token.service';

import Swal from 'sweetalert2';

// Componentes
import { InactivityDialogComponent } from './components/inactivity-dialog.component';
import { environment } from '@src/environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  animations: [
    trigger('routeAnimations', [
      transition('* <=> *', [
        query(':enter', [style({ opacity: 0 })], { optional: true }),
        query(':enter', [animate('300ms ease-in-out', style({ opacity: 1 }))], {
          optional: true,
        }),
      ]),
    ]),
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild('sidenav') sidenav!: MatSidenav;

  // --- Propiedades del Componente ---
  showSpinner = false;
  isMobile = false;
  isSigningOut = false;

  user$!: Observable<fromUser.UserResponse | null>;
  isAuthorized$!: Observable<boolean>;
  isLoginRoute$!: Observable<boolean>;
  showMenu$!: Observable<boolean>;

  private subscriptions = new Subscription();
  private inactivitySub: Subscription | null = null;

  constructor(
    private store: Store<fromRoot.State>,
    public router: Router,
    private breakpointObserver: BreakpointObserver,
    public sidenavStateService: SidenavStateService,
    private webSocketService: WebSocketService,
    private inactivityService: InactivityService,
    private dialog: MatDialog,
    private http: HttpClient,
    private authInterceptor: AuthInterceptor,
    private tokenService: TokenService
  ) {}

  ngOnInit(): void {
    // Siempre inicializar las funciones básicas del layout
    this.initUserMonitoring();
    this.initRoutingMonitoring();
    this.initBreakpointMonitoring();

    // Verificar token expirado después de la inicialización básica
    this.authInterceptor.checkAndCleanExpiredToken();

    // Si no hay sesión válida, limpiar todo y redirigir
    if (!this.hasValidSession()) {
      this.cleanupAndRedirect();
      return;
    }

    // Mantenemos la detección de inactividad, ya que es una funcionalidad útil.
    this.initInactivityDetection();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.inactivitySub?.unsubscribe();
    this.webSocketService.cleanup();
    this.inactivityService.stopMonitoring();
  }

  /**
   * Prepara la animación para el cambio de rutas.
   */
  prepareRoute(outlet: RouterOutlet) {
    return outlet?.activatedRouteData?.['animation'] || '';
  }

  /**
   * Maneja el cierre de sesión del usuario de forma segura.
   */
  onSignOut(sidenav?: MatSidenav): void {
    if (this.isSigningOut) return;
    this.isSigningOut = true;

    // Desconectar usuario del sistema de estado antes de limpiar localStorage
    const userStr = localStorage.getItem('user');
    const userId = userStr ? JSON.parse(userStr).id : null;
    const token = localStorage.getItem('token');

    if (userId && token) {
      const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
      this.http
        .post(
          `${environment.url}api/user/disconnect/${userId}`,
          {},
          { headers }
        )
        .subscribe({
          next: () =>
            console.log('✅ Usuario desconectado del sistema de estado'),
          error: (error) =>
            console.error(
              '❌ Error al desconectar usuario del sistema de estado:',
              error
            ),
        });
    }

    sidenav?.close();
    this.inactivityService.stopMonitoring();
    this.sidenavStateService.setSidenavState(false);
    if (this.webSocketService.isConnected()) {
      this.webSocketService.disconnect();
    }
    window.wsConnectionInitiated = false;
    localStorage.clear();
    this.store.dispatch(new fromUser.SignOut());
    this.router.navigate(['/auth/login']);
    this.isSigningOut = false;
  }

  /**
   * Verifica si existe un token al cargar la app. Si no, redirige al login.
   */
  private checkToken(): void {
    const token = localStorage.getItem('token');
    if (!token && !window.location.pathname.includes('/auth/login')) {
      this.router.navigate(['/auth/login']);
    }
  }

  /**
   * Inicializa la monitorización del estado de usuario desde el Store (NGRX).
   */
  private initUserMonitoring(): void {
    this.user$ = this.store.pipe(select(fromUser.getUser));
    this.isAuthorized$ = this.store.pipe(select(fromUser.getIsAuthorized));
    this.store.dispatch(new fromUser.Init());

    // Cuando el usuario esté autorizado, iniciamos el monitoreo de inactividad.
    this.subscriptions.add(
      this.isAuthorized$.pipe(filter((isAuth) => isAuth)).subscribe(() => {
        this.inactivityService.startMonitoring();
      })
    );
  }

  /**
   * Monitoriza los cambios de ruta para lógica específica (ej. ocultar menú en login).
   */
  private initRoutingMonitoring(): void {
    const routeEvents$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    );

    this.isLoginRoute$ = routeEvents$.pipe(
      map((event) => event.url.includes('/auth/login'))
    );
    this.showMenu$ = routeEvents$.pipe(
      map((event) => !event.url.includes('/auth/login'))
    );

    this.subscriptions.add(
      this.isLoginRoute$.subscribe((isLogin) => {
        if (isLogin) {
          this.sidenav?.close();
          this.sidenavStateService.setSidenavState(false);
        }
      })
    );
  }

  /**
   * Se adapta a los cambios de tamaño de pantalla para el menú lateral.
   */
  private initBreakpointMonitoring(): void {
    this.subscriptions.add(
      this.breakpointObserver
        .observe(['(max-width: 768px)'])
        .subscribe((result) => {
          this.isMobile = result.matches;
          if (!this.isLoginRoute$) {
            this.sidenavStateService.setSidenavState(!this.isMobile);
          }
        })
    );
  }

  /**
   * Inicia el monitoreo de inactividad del usuario.
   */
  private initInactivityDetection(): void {
    this.inactivitySub = this.inactivityService.onInactivityDetected$.subscribe(
      () => {
        if (localStorage.getItem('token')) {
          this.openInactivityDialog();
        }
      }
    );
  }

  /**
   * Abre el diálogo de inactividad y maneja la respuesta.
   */
  private openInactivityDialog(): void {
    this.inactivityService.stopMonitoring();

    const dialogRef = this.dialog.open(InactivityDialogComponent, {
      disableClose: true,
      width: '400px',
    });

    dialogRef.afterClosed().subscribe((userResponded) => {
      if (userResponded) {
        this.inactivityService.startMonitoring();
      } else {
        this.onSignOut();
      }
    });
  }

  /**
   * Verifica si hay una sesión válida
   */
  private hasValidSession(): boolean {
    return this.tokenService.hasValidSession();
  }

  /**
   * Limpia todo y redirige al login sin mostrar modal
   */
  private cleanupAndRedirect(): void {
    console.log('🔒 No hay sesión válida, limpiando y redirigiendo...');

    // Limpiar servicios
    this.inactivityService.stopMonitoring();
    if (this.webSocketService.isConnected()) {
      this.webSocketService.disconnect();
    }

    // Limpiar storage
    this.tokenService.silentLogout();

    // Limpiar estado de la aplicación
    this.store.dispatch(new fromUser.SignOut());

    // Redirigir al login después de un pequeño delay para asegurar que el layout se renderice
    setTimeout(() => {
      this.router.navigate(['/auth/login']);
    }, 100);
  }
}
